import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { wellnessService } from '../../../services/wellnessService';

const ServiceUniverse = () => {
  const navigate = useNavigate();
  const [hoveredCard, setHoveredCard] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedDescriptions, setExpandedDescriptions] = useState({});

  function Treatments({ service }) {
    const [showAll, setShowAll] = useState(false);

    const treatments = service?.treatments || [];

    // Show only 2 items initially, just like the original code
    const visibleTreatments = showAll ? treatments : treatments?.slice(0, 2);

    return (
      <div className="mb-4">
        <div className="flex flex-wrap gap-1 justify-center">
          {/* Visible chips */}
          {visibleTreatments?.map((treatment, idx) => (
            <span
              key={idx}
              className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full"
            >
              {treatment}
            </span>
          ))}

          {/* +N more button — same look as original */}
          {!showAll && treatments?.length > 2 && (
            <span
              onClick={() => setShowAll(true)}
              className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full cursor-pointer hover:bg-muted/80"
            >
              +{treatments?.length - 2} more
            </span>
          )}

          {/* Optional: Show less */}
          {showAll && treatments?.length > 2 && (
            <span
              onClick={() => setShowAll(false)}
              className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full cursor-pointer hover:bg-muted/80"
            >
              Show less
            </span>
          )}
        </div>
      </div>
    );
  }

  // Toggle description expansion
  const toggleDescription = (serviceId) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [serviceId]: !prev[serviceId]
    }));
  };

  // Check if description needs truncation (more than 5 lines ~ 200 characters)
  const needsTruncation = (description) => {
    return description && description.length > 200;
  };

  // Get truncated description
  const getTruncatedDescription = (description) => {
    if (!description) return '';
    return description.length > 200 ? description.substring(0, 200) + '...' : description;
  };

  // Fetch services from database
  useEffect(() => {
    fetchServices();
  }, []);

  // Handle hash-based category selection (e.g., #service-universe-all)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.startsWith('#service-universe-')) {
      const category = hash.replace('#service-universe-', '');
      if (category) {
        setSelectedCategory(category);
      }
    }
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔍 Fetching wellness services for Service Universe...');

      const data = await wellnessService.getWellnessServices();
      console.log('✅ Fetched services:', data?.length);

      // Transform database format to component format
      const transformedServices = data.map(service => ({
        id: service.id,
        title: service.name,
        description: service.description,
        benefits: Array.isArray(service.benefits) ? service.benefits : [],
        contraindications: Array.isArray(service.contraindications) ? service.contraindications : [],
        icon: service.icon_name || 'Sparkles',
        image: service.image_url,
        price: service.base_price ? `From ₹${service.base_price}` : 'Contact for pricing',
        treatments: Array.isArray(service.treatments) ? service.treatments : [],
        gradient: service.gradient_class || 'from-purple-400/20 to-pink-400/20',
        slug: service.slug,
        category: service.category,
        duration: service.duration_minutes,
        therapistsRequired: service.therapists_required
      }));

      setServices(transformedServices);
    } catch (err) {
      console.error('❌ Error fetching services:', err);
      setError('Failed to load services. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Get unique categories from services
  const categories = ['all', ...new Set(services.map(s => s.category))];

  // Category display names
  const categoryNames = {
    'all': 'All Services',
    'rejuvenation': 'Rejuvenation Therapies',
    'pain-relief': 'Pain-Relief Therapies',
    'allergy-sinusitis': 'Allergy & Sinusitis',
    'anti-stress': 'Anti-Stress Therapies',
    'anti-obesity': 'Anti-Obesity Treatments',
    'muscle-building': 'Muscle-Building',
    'post-natal': 'Post-Natal Care'
  };

  // Filter services by selected category
  const filteredServices = selectedCategory === 'all'
    ? services.filter(s => s.slug !== '')
    : services.filter(s => s.category === selectedCategory && s.slug !== '');

  // Handle book service button click
  const handleBookService = (service) => {
    console.log('📅 Booking service:', service.title);
    // Navigate to booking page with pre-selected service
    navigate(`/booking?service=${service.slug}`);
  };

  // Hardcoded services array (will be removed after database migration)
  const hardcodedServices = [
    {
      id: 1,
      title: "Abhyanga - Rejuvenation",
      description: "A full-body warm herbal oil massage combined with Herbal Steambath that improves circulation, relaxes muscles, and balances the nervous system.",
      icon: "Droplets",
      image: "https://media.istockphoto.com/id/169940622/photo/caucasian-enjoying-an-ayuverdic-massage.webp?a=1&b=1&s=612x612&w=0&k=20&c=89kz4d02B_E9wDCz31MRAzxT7tmht09xwj9LC74jgZY=",
      price: "From ₹1499",
      treatments: ["General Wellness", "Pain Relief", "Muscle Toning", "Pre-Panchakarma"],
      gradient: "from-blue-400/20 to-cyan-400/20"
    },
    {
      id: 2,
      title: "Shirodhara - Herbal Oil Stream Therapy",
      description: "A continuous flow of herbal oil over the forehead to calm the mind and regulate stress hormones.",
      icon: "ShowerHead",
      image: "https://media.istockphoto.com/id/636436468/photo/alternative-medicine.webp?a=1&b=1&s=612x612&w=0&k=20&c=0oYpGGPI5qcZtD2bGxCQYaHZMzhfZ9WqWbHVFXsYR-o=",
      price: "From ₹2499",
      treatments: ["Insomnia", "Tension Headaches", "Auto-Immune Issues", "Keratin Treatment"],
      gradient: "from-purple-400/20 to-pink-400/20"
    },
    {
      id: 3,
      title: "Kati Basti - Warm Medicated Oil Retension on Lowback",
      description: "Professional makeup for every occasion and style",
      icon: "Container",
      image: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRrZQDFp4eM6PyvEbJNXjXkGv-SFxGO3zZdrg&s",

      price: "From ₹2499",
      treatments: ["Lowback Pain", "Sciatica"],
      gradient: "from-rose-400/20 to-orange-400/20"
    },
    {
      id: 4,
      title: "Greeva Basti - Warm Medicated Oil Retension on Neck",
      description: "Luxurious manicures and pedicures with artistic flair",
      icon: "ArrowBigUp",
      image: "data:image/jpeg;base64,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",
      price: "From ₹2499",
      treatments: ["Neck Pain", "Disc Bulge",],
      gradient: "from-emerald-400/20 to-teal-400/20"
    },
    {
      id: 5,
      title: "Janu Basti - Knee Joint Rejuvenating Therapy",
      description: "Warm medicated oil is localized over knee joints to lubricate and strengthen them.",
      icon: "Target",
      image: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQ6058mpn9VWL_gnEh7WpGmyo2-i66pKURFww&s",
      price: "From ₹2499",
      treatments: ["Osteo-Arthritis", "Ligament Injuries",],
      gradient: "from-amber-400/20 to-yellow-400/20"
    },
    {
      id: 6,
      title: "Ela-Kizhi: Patra Pinda Sweda: Herbal Poultice",
      description: "Hot herbal leaf poultices are applied to inflamed or stiff areas after Abhyanga.",
      icon: "Leaf",
      image: "data:image/jpeg;base64,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",
      price: "From ₹2499",
      treatments: ["Muscle Spasms", "Tennis Elbow", "Joint-Stiffness", "Tinting"],
      gradient: "from-violet-400/20 to-indigo-400/20"
    },
    {
      id: 7,
      title: "Pizhichil: Herbal Oilbath Therapy",
      description: "A continuous stream of warm herbal oils to regain Dosha balance is poured over the body along with synchronized massage.",
      icon: "Waves",
      image: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR1VoumFjZ8Enj4CtpnYM_7kexE7qBYfYYZUg&s",
      price: "From ₹3499",
      treatments: ["Muscular Stiffness", "Chronic Inflammation", "Nerve Compression Pain", "Tingling Nerve sensation"],
      gradient: "from-violet-400/20 to-indigo-400/20"
    },
    {
      id: 8,
      title: "Udvartana - Herbal Powder Detox Massage",
      description: "A dry herbal massage for lymphatic drainage and metabolic activation.",
      icon: "Sparkles",
      image: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSkH8aJ5d6Y7Vl26jr4jmnM1mLluZ7QgLeYpA&s",
      price: "From ₹2499",
      treatments: ["Obesity", "PCOS", "hypothyroidism", "Cellulite"],
      gradient: "from-purple-400/20 to-pink-400/20"
    },
    {
      id: 9,
      title: "Njavara Kizhi - Rice & Milk Bundle Rejuvenation",
      description: "Warm bundles of medicated rice cooked in herbal decoction and milk nourish muscles and nerves.",
      icon: "CircleDotDashed",
      image: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQXu9UenyMN29fehk6eRv7eV2pTpKwmlE-vjA&s",
      price: "From ₹2999",
      treatments: ["Muscle wasting", "Disc Degeneration", "Muscle stiffness around joints",],
      gradient: "from-purple-400/20 to-pink-400/20"
    },
  ];

  // Loading state
  if (loading) {
    return (
      <section className="py-20 px-4 bg-gradient-to-b from-background to-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="mt-4 text-muted-foreground">Loading services...</p>
          </div>
        </div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section className="py-20 px-4 bg-gradient-to-b from-background to-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <p className="text-destructive">{error}</p>
            <Button onClick={fetchServices} className="mt-4">Try Again</Button>
          </div>
        </div>
      </section>
    );
  }
  {/* Control the size of images */ }
  const getSizedImage = (url) => {
    if (!url) return '';
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}w=900&h=600&fit=crop`;
  };

  return (
    <section id="service-universe" className="py-20 px-4 bg-gradient-to-b from-background to-muted/30">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="font-headline text-3xl md:text-5xl font-bold mb-6 text-morph">
            Service Universe
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Explore our evidence-informed Ayurveda protocols for autoimmune healing, spine health, and joint restoration—shaped by decades of specialized clinical experience.
          </p>
        </motion.div>

        {/* Category Filter */}
        {categories.length > 1 && (
          <motion.div
            className="flex flex-wrap justify-center gap-3 mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full transition-all duration-300 ${selectedCategory === category
                  ? 'bg-primary text-primary-foreground shadow-lg scale-105'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
              >
                {categoryNames[category] || category}
              </button>
            ))}
          </motion.div>
        )}

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredServices?.map((service, index) => (
            <motion.div
              key={service?.id}
              className="group relative"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              onHoverStart={() => setHoveredCard(service?.id)}
              onHoverEnd={() => setHoveredCard(null)}
            >
              <motion.div
                className={`glass-card p-6 h-full cursor-pointer transform-3d ${service?.gradient} bg-gradient-to-br`}
                whileHover={{
                  scale: 1.02,
                  rotateX: 5,
                  rotateY: 5,
                  z: 50
                }}
                transition={{ duration: 0.3 }}
              >
                {/* Service Icon */}
                <motion.div
                  className="w-16 h-16 bg-gradient-to-r from-primary to-accent rounded-full flex items-center justify-center mb-6 mx-auto"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  <Icon name={service?.icon} size={28} className="text-white" />
                </motion.div>

                {/* Service Image */}
                <div className="relative w-full h-[260px] overflow-hidden rounded-2xl">
                  <Image
                    src={getSizedImage(service?.image)}
                    alt={service?.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

                  {/* Before/After Preview 
                  <motion.div
                    className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    initial={false}
                    animate={hoveredCard === service?.id ? { opacity: 1 } : { opacity: 0 }}
                  >
                    <div className="flex gap-2">
                      <div className="text-center">
                        <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white/50 mb-1">
                          <Image
                            src={service?.beforeAfter?.before}
                            alt="Before"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <span className="text-xs text-white font-medium">Before</span>
                      </div>
                      <div className="flex items-center">
                        <Icon name="ArrowRight" size={16} className="text-white" />
                      </div>
                      <div className="text-center">
                        <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white/50 mb-1">
                          <Image
                            src={service?.beforeAfter?.after}
                            alt="After"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <span className="text-xs text-white font-medium">After</span>
                      </div>
                    </div>
                  </motion.div> */}
                </div>

                {/* Service Content */}
                <div className="text-center">
                  <h3 className="font-headline text-xl font-semibold mb-3 text-foreground">
                    {service?.title}
                  </h3>

                  {/* Description with Read More/Less */}
                  <div className="text-left mb-3">
                    {/* Main Description */}
                    <div className="text-muted-foreground text-sm leading-relaxed mb-2">
                      <div className={expandedDescriptions[service?.id] ? '' : 'line-clamp-3'}>
                        {service?.description?.split('\n').map((paragraph, idx) => (
                          paragraph.trim() && (
                            <p key={idx} className="mb-2 last:mb-0">
                              {paragraph.trim()}
                            </p>
                          )
                        ))}
                      </div>
                    </div>

                    {/* Benefits Section - shown when expanded */}
                    {expandedDescriptions[service?.id] && service?.benefits?.length > 0 && (
                      <div className="mt-4 mb-3 bg-green-50 border border-green-200 rounded-lg p-3">
                        <h4 className="text-sm font-bold text-green-900 mb-2 flex items-center gap-1.5">
                          <Icon name="CheckCircle" size={16} className="text-green-600" />
                          Benefits
                        </h4>
                        <ul className="text-xs text-green-800 space-y-1 pl-5">
                          {service.benefits.map((benefit, idx) => (
                            <li key={idx} className="list-disc leading-relaxed">{benefit}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Contraindications Section - shown when expanded */}
                    {expandedDescriptions[service?.id] && service?.contraindications?.length > 0 && (
                      <div className="mt-3 mb-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <h4 className="text-sm font-bold text-amber-900 mb-2 flex items-center gap-1.5">
                          <Icon name="AlertCircle" size={16} className="text-amber-600" />
                          Contraindications
                        </h4>
                        <ul className="text-xs text-amber-800 italic space-y-1 pl-5">
                          {service.contraindications.map((contra, idx) => (
                            <li key={idx} className="list-disc leading-relaxed">{contra}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Read More/Less Button */}
                    {(needsTruncation(service?.description) || service?.benefits?.length > 0 || service?.contraindications?.length > 0) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleDescription(service?.id);
                        }}
                        className="text-primary hover:text-primary/80 text-xs font-medium mt-1 inline-flex items-center gap-1"
                      >
                        {expandedDescriptions[service?.id] ? (
                          <>
                            Read Less <Icon name="ChevronUp" size={14} />
                          </>
                        ) : (
                          <>
                            Read More <Icon name="ChevronDown" size={14} />
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Treatment List */}
                  <div className="text-center">
                    <Treatments service={service} />
                  </div>

                  {/* Metadata: Duration and Therapists */}
                  <div className="flex justify-center gap-4 mb-3 text-xs text-muted-foreground">
                    {service?.duration && (
                      <div className="flex items-center gap-1">
                        <Icon name="Clock" size={14} className="text-primary" />
                        <span>{service.duration} mins</span>
                      </div>
                    )}
                    {service?.therapistsRequired && (
                      <div className="flex items-center gap-1">
                        <Icon name="Users" size={14} className="text-primary" />
                        <span>{service.therapistsRequired} {service.therapistsRequired === 1 ? 'Therapist' : 'Therapists'}</span>
                      </div>
                    )}
                  </div>

                  {/* Price */}
                  <div className="text-lg font-semibold text-primary mb-4">
                    {service?.price}
                  </div>

                  {/* CTA Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full border-primary/30 text-primary hover:bg-primary/10"
                    iconName="Calendar"
                    iconPosition="left"
                    iconSize={16}
                    onClick={() => handleBookService(service)}
                  >
                    Book Service
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
        </motion.div>
      </div>
    </section>
  );
};

export default ServiceUniverse;